import type { AzDanceBookingSummaryParticipantDto } from '@/api/api-client/service';
import { deriveChampionshipFlagsFromSummaryParticipant } from '@/lib/registeredParticipantChampionshipDisplay';
import {
    RegisteredParticipant,
    type UserProfile,
    useRegistrationStore,
} from "@/state/stores/registrationStore";
import { normalizeCountryToIso } from "@/lib/countries/countryLabels";
import {
    decodeJwtSub,
    getAzDanceBookingToken,
} from "@/lib/azdanceBookingSession";
import {
    resolveParticipantPlace,
    resolvePartner2Place,
} from "@/lib/participantPlaceDisplay";
import { isSimpleValidEmail } from "@/lib/validation/email";
import { parseOptionalAge } from "@/lib/mapRegisteredParticipantToAzdanceApi";
import { draftParticipantDedupKey } from "@/lib/registeredParticipantDedupe";
import { normalizeChampionshipCategoryForUi } from "@/lib/participantFieldOptions";
import type { ParticipantFormData } from "./registrationFlowTypes";

type ContactType = ParticipantFormData["contactType"];

function resolvePartner2StoredRole(
    p: ParticipantFormData,
): "leader" | "follower" | "switch" {
    const r = p.partner2Role;
    if (r === "leader" || r === "follower" || r === "switch") return r;
    if (p.role === "leader") return "follower";
    if (p.role === "follower") return "leader";
    return "follower";
}

function resolvePartner2RoleFromPrimary(
    primaryRole: RegisteredParticipant["role"],
): "leader" | "follower" | "switch" {
    if (primaryRole === "leader") return "follower";
    if (primaryRole === "follower") return "leader";
    return "follower";
}

const SERVER_PARTICIPANT_ULID_RE = /^[0-9A-HJKMNP-TV-Z]{26}$/i;

function mergedChampionshipHasPersistedCopartner(
    p: RegisteredParticipant,
): boolean {
    if (!p.isChampionshipParticipant || p.isSolo) return false;
    const link = p.partnerId?.trim();
    if (!link || link === p.id) return false;
    if (!SERVER_PARTICIPANT_ULID_RE.test(link)) return false;
    const em = p.partner2Email?.trim();
    return (
        !!p.partner2FirstName?.trim() &&
        !!p.partner2LastName?.trim() &&
        !!em &&
        isSimpleValidEmail(em)
    );
}

/**
 * Одна карточка с полями partner2* → два {@link RegisteredParticipant} с взаимными `partnerId`
 * (для POST `booking/participants` и стора). Уже разнесённая пара (`partnerId` / `isPartner2`) не трогаем.
 * Если `partnerId` — ULID соучастника на брони и заполнены partner2*, отдаём **две** строки (оба id в POST).
 */
export function expandChampionshipCopartnerForUpsert(
    p: RegisteredParticipant,
): RegisteredParticipant[] {
    if (p.isPartner2) return [p];

    if (mergedChampionshipHasPersistedCopartner(p)) {
        const copartnerId = p.partnerId!.trim();
        const c2 = buildStoredContact(
            (p.partner2ContactType as ContactType) || "",
            p.partner2ContactValue || "",
            p.partner2Instagram,
        );
        const p2Role = p.partner2Role;
        const copartnerRole =
            p2Role === "leader" ||
            p2Role === "follower" ||
            p2Role === "switch"
                ? p2Role
                : resolvePartner2RoleFromPrimary(p.role);
        const participant2: RegisteredParticipant = {
            id: copartnerId,
            firstName: p.partner2FirstName!.trim(),
            lastName: p.partner2LastName!.trim(),
            age: p.partner2Age,
            email: p.partner2Email!.trim(),
            phone: (p.partner2Phone ?? "").trim() || p.phone || "",
            country: p.partner2Country || "",
            city: p.partner2City || "",
            location: "",
            gender: p.partner2Gender || "",
            role: copartnerRole,
            level: p.partner2Level || "",
            contactType: c2.contactType,
            contactValue: c2.contactValue,
            instagram: c2.instagram,
            isChampionshipParticipant: true,
            registrationMode: 'championship',
            isSolo: false,
            lookingForPartner: null,
            category: normalizeChampionshipCategoryForUi(p.category),
            partnerId: p.id,
            isPartner2: true,
            registeredAt: p.registeredAt,
            paymentStatus: p.paymentStatus,
            totalAmount: p.totalAmount,
        };

        const participant1: RegisteredParticipant = {
            ...p,
            partnerId: copartnerId,
            partner2FirstName: undefined,
            partner2LastName: undefined,
            partner2Age: undefined,
            partner2Email: undefined,
            partner2Phone: undefined,
            partner2City: undefined,
            partner2Country: undefined,
            partner2Location: undefined,
            partner2Gender: undefined,
            partner2Level: undefined,
            partner2Role: undefined,
            partner2ContactType: undefined,
            partner2ContactValue: undefined,
            partner2Instagram: undefined,
        };

        return [participant1, participant2];
    }

    /** Client temp ids / numeric indices must not block splitting by embedded `partner2*` fields. */
    const rawPid = p.partnerId?.trim();
    if (rawPid && SERVER_PARTICIPANT_ULID_RE.test(rawPid)) return [p];

    const {
        partner2FirstName,
        partner2LastName,
        partner2Email,
        partner2Age,
        partner2Phone,
        partner2Country,
        partner2City,
        partner2Gender,
        partner2Level,
        partner2ContactType,
        partner2ContactValue,
        partner2Instagram,
    } = p;

    if (
        !p.isChampionshipParticipant ||
        p.isSolo ||
        !partner2FirstName?.trim() ||
        !partner2LastName?.trim() ||
        !partner2Email?.trim() ||
        !isSimpleValidEmail(partner2Email)
    ) {
        return [p];
    }

    const partner2Id = `${Date.now()}-partner2-${Math.random()
        .toString(36)
        .substr(2, 9)}`;
    const c2 = buildStoredContact(
        partner2ContactType || "",
        partner2ContactValue || "",
        partner2Instagram,
    );

    const participant2: RegisteredParticipant = {
        id: partner2Id,
        firstName: partner2FirstName.trim(),
        lastName: partner2LastName.trim(),
        age: partner2Age,
        email: partner2Email.trim(),
        phone: partner2Phone?.trim() || p.phone || "",
        country: partner2Country || "",
        city: partner2City || "",
        location: "",
        gender: partner2Gender || "",
        role: resolvePartner2RoleFromPrimary(p.role),
        level: partner2Level || "",
        contactType: c2.contactType,
        contactValue: c2.contactValue,
        instagram: c2.instagram,
        isChampionshipParticipant: true,
        registrationMode: 'championship',
        isSolo: false,
        lookingForPartner: null,
        category: normalizeChampionshipCategoryForUi(p.category),
        partnerId: p.id,
        isPartner2: true,
        registeredAt: p.registeredAt,
        paymentStatus: p.paymentStatus,
        totalAmount: p.totalAmount,
    };

    const participant1: RegisteredParticipant = {
        ...p,
        partnerId: partner2Id,
    };

    return [participant1, participant2];
}

function buildStoredContact(
    contactType: ContactType,
    contactValue: string,
    instagramLegacy?: string,
): { contactType: ContactType; contactValue: string; instagram: string } {
    const cv = (contactValue || "").trim();
    const igLegacy = (instagramLegacy || "").trim().replace(/^@/, "");
    const type =
        contactType || (cv ? "telegram" : igLegacy ? "instagram" : "");
    const value = cv || igLegacy;
    const instagram = type === "instagram" ? value : igLegacy || "";
    return {
        contactType: type,
        contactValue: value,
        instagram,
    };
}

function resolvePreferPrimaryParticipantId(): string | null {
    const s = useRegistrationStore.getState();
    const token = getAzDanceBookingToken();
    const sub = token ? decodeJwtSub(token) : null;
    return (
        sub?.trim() ||
        s.cabinetOwnerParticipantId?.trim() ||
        s.currentUser?.id?.trim() ||
        null
    );
}

function pickPrimaryAndCopartner(
    a: RegisteredParticipant,
    b: RegisteredParticipant,
    preferId: string | null,
): [RegisteredParticipant, RegisteredParticipant] {
    if (preferId) {
        if (a.id === preferId) return [a, b];
        if (b.id === preferId) return [b, a];
    }
    return a.id.localeCompare(b.id) <= 0 ? [a, b] : [b, a];
}

function foldCopartnerIntoChampionshipRow(
    primary: RegisteredParticipant,
    copart: RegisteredParticipant,
): RegisteredParticipant {
    const c2 = buildStoredContact(
        (copart.contactType as ContactType) || "",
        copart.contactValue || "",
        copart.instagram,
    );
    const copLevel = copart.level || "";
    const copGender = copart.gender || "";
    const copRole = copart.role || "";
    const partner2Level =
        copLevel === "beginner" ||
        copLevel === "intermediate" ||
        copLevel === "advanced" ||
        copLevel === "professional"
            ? copLevel
            : "";
    const partner2Gender: RegisteredParticipant["partner2Gender"] =
        copGender === "mr" ||
        copGender === "ms" ||
        copGender === "male" ||
        copGender === "female" ||
        copGender === "other" ||
        copGender === "prefer_not_to_say"
            ? copGender
            : "";
    const partner2Role =
        copRole === "leader" ||
        copRole === "follower" ||
        copRole === "switch"
            ? copRole
            : "";
    return {
        ...primary,
        isChampionshipParticipant: true,
        registrationMode: 'championship',
        isSolo: false,
        partnerId: copart.id,
        lookingForPartner: primary.lookingForPartner ?? null,
        category: normalizeChampionshipCategoryForUi(
            primary.category || copart.category || "",
        ),
        partner2FirstName: copart.firstName,
        partner2LastName: copart.lastName,
        partner2Age: copart.age,
        partner2Email: copart.email,
        partner2Phone: copart.phone || "",
        partner2City: copart.city || "",
        partner2Country: copart.country || "",
        partner2Location: "",
        partner2Gender,
        partner2Level,
        partner2Role,
        partner2ContactType: c2.contactType,
        partner2ContactValue: c2.contactValue,
        partner2Instagram: c2.instagram,
    };
}

/**
 * Две строки с взаимным `partnerId` (пара чемпионата с бэка) → одна строка с полями `partner2*`
 * для анкеты воронки. В Zustand после {@link pullBookingParticipantsIntoStore} пары **не** сливаются.
 */
export function mergeCopartnerRegisteredParticipants(
    list: RegisteredParticipant[],
): RegisteredParticipant[] {
    if (list.length < 2) return list;

    const byId = new Map(list.map((p) => [p.id, p]));
    const consumed = new Set<string>();
    const out: RegisteredParticipant[] = [];
    const preferId = resolvePreferPrimaryParticipantId();

    for (const p of list) {
        if (consumed.has(p.id)) continue;
        const link = p.partnerId?.trim();
        if (link && byId.has(link)) {
            const q = byId.get(link)!;
            if (q.id !== p.id) {
                const [primary, copart] = pickPrimaryAndCopartner(
                    p,
                    q,
                    preferId,
                );
                consumed.add(primary.id);
                consumed.add(copart.id);
                out.push(foldCopartnerIntoChampionshipRow(primary, copart));
                continue;
            }
        }
        out.push(p);
    }
    return out;
}

/**
 * After expanding a championship row into one or two store rows, drop any
 * `others` rows whose participant id already appears in `expanded`. Email is
 * not used for identity (many flows share one contact email).
 */
export function mergeExpandedChampionshipPairDedupingOthers(
    others: RegisteredParticipant[],
    expanded: RegisteredParticipant[],
): RegisteredParticipant[] {
    const expandedIds = new Set(
        expanded.map((p) => p.id.trim()).filter((id) => id.length > 0),
    );
    const expandedDraftKeys = new Set<string>();
    for (const p of expanded) {
        const k = draftParticipantDedupKey(p);
        if (k) expandedDraftKeys.add(k);
    }
    const kept = others.filter((row) => {
        if (expandedIds.has(row.id.trim())) return false;
        const k = draftParticipantDedupKey(row);
        if (k && expandedDraftKeys.has(k)) return false;
        return true;
    });
    return [...kept, ...expanded];
}

export function buildRegisteredParticipantBlocks(
    formRows: ParticipantFormData[],
): RegisteredParticipant[][] {
    return formRows.map((p) => {
        const c1 = buildStoredContact(
            p.contactType,
            p.contactValue,
            p.instagram,
        );
        const participant1: RegisteredParticipant = {
            id:
                p.id ??
                `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`,
            partnerId: p.partnerId?.trim() || undefined,
            firstName: p.firstName,
            lastName: p.lastName,
            age: parseOptionalAge(p.age),
            email: p.email,
            phone: p.phone,
            country: p.country || "",
            city: p.city || "",
            location: "",
            gender: p.gender,
            role: p.role,
            level: p.level,
            contactType: c1.contactType,
            contactValue: c1.contactValue,
            instagram: c1.instagram,
            isChampionshipParticipant: p.isChampionshipParticipant,
            isSolo: p.isSolo,
            lookingForPartner: p.lookingForPartner ?? null,
            category: normalizeChampionshipCategoryForUi(p.category),
            partner2FirstName: p.partner2FirstName,
            partner2LastName: p.partner2LastName,
            partner2Age: parseOptionalAge(p.partner2Age),
            partner2Email: p.partner2Email,
            partner2Phone: p.partner2Phone,
            partner2City: p.partner2City || "",
            partner2Country: p.partner2Country || "",
            partner2Location: "",
            partner2Gender: p.partner2Gender,
            partner2Level: p.partner2Level,
            partner2Role: p.partner2Role,
            partner2ContactType: p.partner2ContactType,
            partner2ContactValue: p.partner2ContactValue,
            partner2Instagram: p.partner2Instagram,
        };

        const expanded = expandChampionshipCopartnerForUpsert(participant1);
        if (expanded.length === 2) {
            const [p1, p2] = expanded;
            return [p1, { ...p2, role: resolvePartner2StoredRole(p) }];
        }
        return expanded;
    });
}

/**
 * Участник из GET booking/participants или GET booking/summary → формат стора registration-flow.
 */
export function mapAzDanceSummaryParticipantToRegisteredParticipant(
    sp: AzDanceBookingSummaryParticipantDto,
): RegisteredParticipant {
    const ext = sp as AzDanceBookingSummaryParticipantDto & {
        countryCode?: string;
        city?: string;
        role?: RegisteredParticipant['role'];
        level?: RegisteredParticipant['level'];
        gender?: string;
        contactType?: string;
        contactValue?: string;
        age?: string | number;
    };
    const genderUi: RegisteredParticipant['gender'] =
        ext.gender === 'male' ||
        ext.gender === 'female' ||
        ext.gender === 'other' ||
        ext.gender === 'prefer_not_to_say'
            ? ext.gender
            : '';
    const apiCt = (ext.contactType || 'telegram').toLowerCase();
    const contactTypeUi: RegisteredParticipant['contactType'] =
        apiCt === 'whatsapp'
            ? 'whatsapp'
            : apiCt === 'instagram'
              ? 'instagram'
              : 'telegram';
    const cv = (ext.contactValue ?? '').trim();
    const instagram = contactTypeUi === 'instagram' ? cv : '';
    const { isChampionshipParticipant, isSolo } =
        deriveChampionshipFlagsFromSummaryParticipant(sp);
    const registrationModeRaw = (sp as { registrationMode?: string })
        .registrationMode;
    const registrationMode =
        registrationModeRaw === 'championship' ||
        registrationModeRaw === 'default'
            ? registrationModeRaw
            : undefined;
    return {
        id: sp.id,
        firstName: sp.firstName || '',
        lastName: sp.lastName || '',
        age: parseOptionalAge(ext.age),
        email: sp.email || '',
        phone: sp.phone || '',
        country: (ext.countryCode || '').trim(),
        city: (ext.city || '').trim(),
        location: '',
        gender: genderUi,
        role: ext.role || '',
        level: ext.level || '',
        contactType: contactTypeUi,
        contactValue: cv,
        instagram,
        registrationMode,
        isChampionshipParticipant,
        isSolo,
        lookingForPartner: sp.lookingForPartner ?? null,
        partnerId: sp.copartnerId?.trim() || undefined,
        registeredAt: new Date().toISOString(),
    };
}

/** ЛК: участник из профиля, если его ещё нет в списке сохранённых (например после гидрации). */
export function userProfileToRegisteredParticipant(
    u: UserProfile,
): RegisteredParticipant {
    const { city, countryIso } = resolveParticipantPlace({
        city: u.city,
        country: u.country,
        location: u.location,
    });
    const country = countryIso || u.country || "";
    return {
        id: u.id,
        firstName: u.firstName,
        lastName: u.lastName,
        age: u.age,
        email: u.email,
        phone: u.phone,
        location: "",
        country,
        city: city || u.city || "",
        gender: u.gender,
        role: u.role,
        level: u.level,
        contactType: u.contactType,
        contactValue: u.contactValue,
        instagram: u.instagram,
        registeredAt: u.registeredAt,
    };
}

export function mergeCurrentUserIntoRegisteredParticipants(
    registered: RegisteredParticipant[],
    currentUser: UserProfile | null,
): RegisteredParticipant[] {
    if (!currentUser) return registered;
    if (registered.some((p) => p.id === currentUser.id)) return registered;
    return [userProfileToRegisteredParticipant(currentUser), ...registered];
}

export function mapRegisteredParticipantsToParticipantFormData(
    registered: RegisteredParticipant[],
): ParticipantFormData[] {
    const list = mergeCopartnerRegisteredParticipants(registered);
    return list.map((p) => {
        const r1 = resolveParticipantPlace(p);
        let city = r1.city || (p.city ?? "").trim();
        let country = r1.countryIso || (p.country ?? "").trim();
        const n1 = normalizeCountryToIso(country);
        if (n1) country = n1;
        const r2 = resolvePartner2Place(p);
        let partner2City = r2.city || (p.partner2City ?? "").trim();
        let partner2Country = r2.countryIso || (p.partner2Country ?? "").trim();
        const n2 = normalizeCountryToIso(partner2Country);
        if (n2) partner2Country = n2;
        return {
            id: p.id,
            partnerId: p.partnerId,
            firstName: p.firstName || "",
            lastName: p.lastName || "",
            age: p.age != null ? String(p.age) : "",
            email: p.email || "",
            phone: p.phone || "",
            city,
            country,
            gender: p.gender || "",
            role: p.role || "",
            level: p.level || "",
            contactType:
                p.contactType ||
                (p.instagram ? "instagram" : "") ||
                "",
            contactValue:
                p.contactValue ||
                (p.instagram
                    ? p.instagram.replace(/^@/, "")
                    : "") ||
                "",
            instagram: p.instagram || "",
            isChampionshipParticipant: p.isChampionshipParticipant || false,
            isSolo: p.isSolo || false,
            lookingForPartner:
                p.lookingForPartner === true ||
                p.lookingForPartner === false
                    ? p.lookingForPartner
                    : null,
            category: normalizeChampionshipCategoryForUi(p.category),
            partner2FirstName: p.partner2FirstName || "",
            partner2LastName: p.partner2LastName || "",
            partner2Age: p.partner2Age != null ? String(p.partner2Age) : "",
            partner2Email: p.partner2Email || "",
            partner2Phone: p.partner2Phone || "",
            partner2City,
            partner2Country,
            partner2Gender: p.partner2Gender || "",
            partner2Level: p.partner2Level || "",
            partner2Role: p.partner2Role || "",
            partner2ContactType:
                p.partner2ContactType ||
                (p.partner2Instagram ? "instagram" : "") ||
                "",
            partner2ContactValue:
                p.partner2ContactValue ||
                (p.partner2Instagram
                    ? p.partner2Instagram.replace(/^@/, "")
                    : "") ||
                "",
            partner2Instagram: p.partner2Instagram || "",
            isExpanded: false,
        };
    });
}

/** Индекс карточки (строки формы), к которой относится текущий пользователь ЛК. */
export function primaryFormRowIndexForCurrentUser(
    forms: ParticipantFormData[],
    currentUser: UserProfile | null,
): number {
    if (!currentUser || forms.length === 0) return 0;
    const idx = forms.findIndex((row) => row.id === currentUser.id);
    if (idx >= 0) return idx;
    return 0;
}

export function computeInitialPrimaryFormIndex(): number {
    const state = useRegistrationStore.getState();
    const reg = state.registeredParticipants;
    if (reg.length > 0) {
        const ownerId =
            state.cabinetOwnerParticipantId || state.currentUser?.id;
        if (ownerId) {
            const idx = reg.findIndex((p) => p.id === ownerId);
            if (idx >= 0) return idx;
        }
        return 0;
    }
    return 0;
}
